import { ErrorCode, PaymentErrorCodes, RequestErrorCodes } from './codes';

import { SdkErrorOptions, SdkErrorOrigin } from './types';
import { PaymentMethodType } from '../api-types';
import { PrimerFetchResponseInfo } from '../request';

/**
 * Single error class for all SDK errors. `origin` says who should act on the
 * error (fix the integration, contact Primer, retry, or nothing); `code`
 * identifies the specific failure.
 */
export class SdkError extends Error {
  readonly origin: SdkErrorOrigin;
  readonly code: ErrorCode;
  readonly error: unknown;
  /** Never report this error to Datadog (expected outcome, e.g. a user cancellation) */
  readonly skipReporting: boolean;
  readonly suggestion?: string;
  readonly paymentMethod?: PaymentMethodType;
  readonly response?: PrimerFetchResponseInfo;
  /** Analytics-only failure qualifier — see {@link SdkErrorOptions.failureOutcome} */
  readonly failureOutcome?: 'unknown' | 'approval_timeout';
  /** Set once the error has been sent to Datadog, so it is not reported twice */
  reported = false;

  constructor(options: SdkErrorOptions) {
    super(options.message);
    this.name = 'SdkError';
    this.origin = options.origin;
    this.code = options.code ?? RequestErrorCodes.UNEXPECTED;
    this.error = options.error;
    this.skipReporting = options.skipReporting ?? options.origin === 'user';
    this.suggestion = options.suggestion;
    this.paymentMethod = options.paymentMethod;
    this.response = options.response;
    this.failureOutcome = options.failureOutcome;
  }

  toJSON(): SerializedSdkError {
    return {
      name: this.name,
      origin: this.origin,
      message: this.message,
      code: this.code,
      stack: this.stack,
      ...(this.suggestion !== undefined && { suggestion: this.suggestion }),
      ...(this.paymentMethod !== undefined && {
        paymentMethod: this.paymentMethod,
      }),
      ...(this.response !== undefined && { response: this.response }),
      ...(this.error !== undefined && { error: serializeError(this.error) }),
    };
  }

  /**
   * Rebuild an SdkError serialized with {@link SdkError.toJSON}, e.g. after
   * crossing an iframe RPC boundary where class instances don't survive.
   */
  static fromJSON(data: SerializedSdkError): SdkError {
    const error = new SdkError(data);
    if (data.stack) {
      error.stack = data.stack;
    }
    return error;
  }
}

/**
 * Wire format of an SdkError: what {@link SdkError.toJSON} produces and
 * {@link SdkError.fromJSON} accepts.
 */
export interface SerializedSdkError {
  name: string;
  origin: SdkErrorOrigin;
  message: string;
  code: ErrorCode;
  stack?: string;
  suggestion?: string;
  paymentMethod?: PaymentMethodType;
  response?: PrimerFetchResponseInfo;
  error?: unknown;
}

/**
 * The merchant aborted payment creation from an `onBeforePaymentCreate`-style
 * callback. Merchant-initiated (not the shopper), hence `integration` origin;
 * an expected outcome, hence never reported to Datadog.
 */
export const paymentCreationAbortedError = (
  paymentMethod?: PaymentMethodType,
): SdkError =>
  new SdkError({
    origin: 'integration',
    message: 'Payment creation aborted',
    code: PaymentErrorCodes.PAYMENT_CREATION_ABORTED,
    skipReporting: true,
    paymentMethod,
  });

/**
 * Structural SdkError check: `instanceof` alone fails when the error was
 * created by another bundled copy of this class (e.g. sdk-core's dist bundle
 * inside primer-js), so also match on shape.
 */
export const isSdkError = (error: unknown): error is SdkError =>
  error instanceof SdkError ||
  (error instanceof Error &&
    error.name === 'SdkError' &&
    typeof (error as SdkError).code === 'string' &&
    typeof (error as SdkError).origin === 'string');

/** Best-effort plain-object serialization of an unknown nested error */
export const serializeError = (error: unknown): Record<string, unknown> => {
  try {
    const serialized: unknown = JSON.parse(
      JSON.stringify(
        error,
        !isSdkError(error) && error instanceof Error
          ? Object.getOwnPropertyNames(error)
          : undefined,
      ),
    );
    return typeof serialized === 'object' && serialized !== null
      ? (serialized as Record<string, unknown>)
      : { message: String(serialized) };
  } catch {
    return { message: String(error) };
  }
};
